import { zodResolver } from '@hookform/resolvers/zod';
import type { PlanInputFormFixedProduct } from '@vy/types';
import { isNumber } from '@vy/utils';
import type { Resolver } from 'react-hook-form';
import type { PlanInputForm } from '../pages/plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';

/**
 * A discount only counts once it carries a positive amount. The modify panel
 * shows the discount fields unconditionally (no reveal toggle), so a product
 * can briefly hold a partial `discountConfiguration` (a name but no amount, or
 * an amount the user cleared). The schema rejects such a config, which would
 * otherwise block the whole plan from saving — so we drop any discount without
 * a positive amount before validation and before building the API payload.
 */
type DiscountablePriceConfig = { discountConfiguration?: { amountToDeduct?: number } | undefined };

function hasPositiveDiscountAmount(priceConfiguration: DiscountablePriceConfig): boolean {
  const amount = priceConfiguration.discountConfiguration?.amountToDeduct;
  return isNumber(amount) && amount > 0;
}

function stripProductDiscount<T extends PlanInputFormFixedProduct>(product: T): T {
  let changed = false;

  const variants = product.variants.map((variant) => {
    const priceConfiguration = variant.pricing.priceConfiguration as DiscountablePriceConfig | undefined;

    if (!priceConfiguration?.discountConfiguration || hasPositiveDiscountAmount(priceConfiguration)) {
      return variant;
    }

    changed = true;
    return {
      ...variant,
      pricing: {
        ...variant.pricing,
        priceConfiguration: { ...priceConfiguration, discountConfiguration: undefined },
      },
    } as typeof variant;
  });

  return changed ? { ...product, variants } : product;
}

export function stripIncompleteDiscounts(values: PlanInputForm): PlanInputForm {
  const fixedProducts = values.fixedProducts?.map(stripProductDiscount);
  const promotionProducts = values.promotionProducts?.map(stripProductDiscount);

  return {
    ...values,
    ...(fixedProducts ? { fixedProducts } : {}),
    ...(promotionProducts ? { promotionProducts } : {}),
  };
}

/**
 * A zodResolver that strips incomplete discounts before validating, so partial
 * (no reveal-toggle) discount configs never block plan/contract submission.
 * Shared by the Create-plan and Update-contract forms.
 */
export function createDiscountStrippingResolver(
  schema: Parameters<typeof zodResolver>[0],
): Resolver<PlanInputForm, unknown> {
  const resolve = zodResolver(schema) as Resolver<PlanInputForm, unknown>;
  return (values, context, options) => resolve(stripIncompleteDiscounts(values), context, options);
}
